import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthSessionContextValue = {
  session: Session | null;
  user: User | null;
  isLoadingAuth: boolean;
  isAuthed: boolean;
};

const AuthSessionContext = createContext<AuthSessionContextValue>({
  session: null,
  user: null,
  isLoadingAuth: true,
  isAuthed: false,
});

const AUTH_BOOT_TIMEOUT_MS = 5000;

export function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const bootedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const boot = async () => {
      if (bootedRef.current) return;
      bootedRef.current = true;

      try {
        const timeoutPromise = new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), AUTH_BOOT_TIMEOUT_MS),
        );

        const sessionPromise = supabase.auth
          .getSession()
          .then((r) => r.data.session)
          .catch(() => null);

        const s = (await Promise.race([sessionPromise, timeoutPromise])) as Session | null;
        if (isMounted) setSession(s);
      } catch {
        if (isMounted) setSession(null);
      } finally {
        if (isMounted) setIsLoadingAuth(false);
      }
    };

    boot();

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);
      // If auth changes, we are definitely “done booting”.
      setIsLoadingAuth(false);
    });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthSessionContextValue>(() => {
    const user = session?.user ?? null;
    return {
      session,
      user,
      isLoadingAuth,
      isAuthed: !!user,
    };
  }, [session, isLoadingAuth]);

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

export function useAuthSession() {
  return useContext(AuthSessionContext);
}
